import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../core/basic/Button";
import { css, cx } from "../../../../styled-system/css";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../core/basic/Modal";
import { useSimpleCan } from "../../../config/casl/casl";
import AssetSelector from "../../core/blocks/AssetSelector/AssetSelector";
import { useAppSelector } from "../../../redux/hooks";
import { Checkbox } from "../../fields/base/Checkbox";
import { Badge } from "react-bootstrap";
import { useForm, Controller, type SubmitHandler } from "react-hook-form";
import { TextField } from "../../fields/base/TextField";
import { SelectedAssetsPreview, type AssetSection, type AssetSectionLabelSelector } from "./SelectedAssetsPreview";
import { ProductSelector } from "../../core/blocks/ProductSelector/ProductSelector";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "../../../zodios/client";
import { errorToast, successToast } from "../../../utils/toasts";
import { queryClient } from "../../../config/react-query";
import { useWatch } from "react-hook-form";
import { getVideoGenFormSchema, type FormValues } from "./videoGenFormSchema";
import { zodResolver } from "@hookform/resolvers/zod";

const DEFAULT_FORM_DATA: Partial<FormValues> = {
  enableInspirationAssetSelection: false,
  asin: undefined,
  productId: undefined,
  name: "",
  brandLogoAssetIds: [],
  lifestyleImageAssetIds: [],
  videoAssetIds: [],
};

type AssetType = "lifestyleImage" | "logo" | "video";
type AssetField = "lifestyleImageAssetIds" | "brandLogoAssetIds" | "videoAssetIds";

const assetTypeLookup: Record<AssetType, AssetField> = {
  lifestyleImage: "lifestyleImageAssetIds",
  logo: "brandLogoAssetIds",
  video: "videoAssetIds",
};

const assetTypeLabels: Record<AssetType, AssetSectionLabelSelector> = {
  lifestyleImage: ($) => $.advertising["Lifestyle Images"],
  logo: ($) => $.advertising["Brand Logos"],
  video: ($) => $.advertising["Videos"],
};

type VideoGenerationDialogueFormProps = {
  asin?: string;
  productId?: number;
  showModal: boolean;
  setShowModal: (isOpen: boolean) => void;
  initialData?: Partial<FormValues>;
};

type VideoGenerationDialogueProps = {
  asin?: string;
  productId?: number;
};

export const VideoGenerationDialogueForm = ({
  asin,
  productId,
  showModal,
  setShowModal,
  initialData,
}: VideoGenerationDialogueFormProps) => {
  const { t } = useTranslation(["advertising", "common", "validation"]);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType>("lifestyleImage");
  const [selectedProductKeys, setSelectedProductKeys] = useState<string[]>([]);

  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const mergedDefaults = useMemo(() => {
    return { ...DEFAULT_FORM_DATA, asin, productId: productId != null ? productId : undefined, ...initialData };
  }, [asin, productId, initialData]);

  const schema = useMemo(() => getVideoGenFormSchema(t), [t]);

  const { control, setValue, handleSubmit, formState, watch } = useForm<FormValues>({
    mode: "all",
    defaultValues: mergedDefaults,
    resolver: zodResolver(schema),
  });

  const enableInspirationAssetSelection = watch("enableInspirationAssetSelection");
  const currentSelections = watch(assetTypeLookup[selectedAssetType]);
  const selectedAssetsByType = {
    lifestyleImage: watch("lifestyleImageAssetIds"),
    logo: watch("brandLogoAssetIds"),
    video: watch("videoAssetIds"),
  };

  const watchedProductId = useWatch({ control, name: "productId" });
  const watchedAsin = useWatch({ control, name: "asin" });
  const selectedProductRowIds =
    selectedProductKeys.length ? selectedProductKeys
    : (asin ?? watchedAsin) ? [`${watchedProductId}:${watchedAsin}`]
    : [];
  const totalSelectedCount =
    selectedAssetsByType.lifestyleImage.length + selectedAssetsByType.logo.length + selectedAssetsByType.video.length;

  const submitVideoGenerationRequest = useMutation({
    mutationFn: async (params: Parameters<typeof apiClient.submitVideoGenerationRequest>[0]) => {
      return await apiClient.submitVideoGenerationRequest(params);
    },
    onSuccess: async () => {
      setShowModal(false);
      successToast({
        message: t(($) => $.advertising["Successfully created video generation request for ASIN {asin}"], { asin }),
      });
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const stringifiedQueryKey = JSON.stringify(query.queryKey);
          return (
            stringifiedQueryKey.includes("videoGenerationRequests") ||
            stringifiedQueryKey.includes("videoGenTaskResults")
          );
        },
      });
    },
    onError: (error) => {
      errorToast({
        message: t(
          ($) => $.advertising["Something went wrong while creating a new video generation request. Please try again."],
        ),
        error,
      });
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    const resolvedProductId = values.productId ?? productId;

    const body = {
      name: values.name,
      gvproduct_id: Number(resolvedProductId),
      image_ids:
        values.enableInspirationAssetSelection ? [...values.brandLogoAssetIds, ...values.lifestyleImageAssetIds] : [],
      video_ids: values.enableInspirationAssetSelection ? values.videoAssetIds : [],
    };

    submitVideoGenerationRequest.mutate(body);
  };

  const updateSelections = (assetType: AssetType, updatedSelections: string[]) => {
    setValue(assetTypeLookup[assetType], updatedSelections, { shouldValidate: true, shouldDirty: true });
  };

  const assetErrorsToDisplay = [
    formState.errors.brandLogoAssetIds?.message,
    formState.errors.lifestyleImageAssetIds?.message,
    formState.errors.videoAssetIds?.message,
  ].filter(Boolean);

  const selectedAssetSections = (Object.keys(selectedAssetsByType) as AssetType[])
    .map<AssetSection>((assetType) => ({
      label: assetTypeLabels[assetType],
      assetIds: selectedAssetsByType[assetType],
      assetType: assetType === "video" ? "VIDEO" : "IMAGE",
      sectionKey: assetType,
    }))
    .filter((section) => section.assetIds.length);

  return (
    <Modal
      isOpen={showModal}
      onOpenChange={(isOpen) => {
        if (!isOpen) setShowModal(false);
      }}
      title={
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Flex gap={"large"} alignItems={"center"}>
            <Badge className={cx(css({ marginY: "auto" }), "bg-trellis-mauve")} pill>
              {t(($) => $.advertising["Beta"])}
            </Badge>
            <h3 className={css({ marginY: "auto", fontSize: "xlarge", fontWeight: "medium" })}>
              {t(($) => $.advertising["Amazon Product Video Generator"])}
            </h3>
          </Flex>
          {asin ?
            <span className={css({ marginY: "auto", paddingX: "large", fontSize: "medium", fontWeight: "medium" })}>
              {t(($) => $.advertising["Generate preview for ASIN {asin}"], { asin })}
            </span>
          : null}
        </Flex>
      }
    >
      <VStack
        className="custom-scroll-container"
        css={{
          gap: "large",
          width: "1000px",
          maxWidth: "90vw",
          paddingX: "xlarge",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)} className={css({ width: "100%", padding: "xlarge" })}>
          <VStack css={{ gap: "large", paddingTop: "large" }}>
            <Controller
              control={control}
              name={"name"}
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...field}
                    errorMessage={fieldState.error?.message}
                    placeholder={t(($) => $.advertising["Enter request name..."])}
                    aria-label={t(($) => $.advertising["Request name"])}
                    label={t(($) => $.advertising["Name"])}
                    css={{ width: "100%" }}
                  />
                );
              }}
            />
            {!asin ?
              <VStack
                className={css({
                  width: "100%",
                  overflowX: "auto",
                })}
              >
                <ProductSelector
                  context={"videoGenerationDialogue"}
                  enableRowSelection={true}
                  enableMultiRowSelection={false}
                  enableSelectAll={false}
                  rowIdAccessor={"product_key"}
                  selectedRowIds={selectedProductRowIds}
                  defaultSelectedColumns={["product_title"]}
                  onSelectionChange={(productKey) => {
                    setSelectedProductKeys(productKey);
                    if (!productKey.length) {
                      setValue("productId", undefined, { shouldValidate: true, shouldDirty: true });
                      setValue("asin", undefined, { shouldValidate: true, shouldDirty: true });
                      setSelectedProductKeys([]);
                      return;
                    }
                    const [selectedProductIdValue, selectedAsinValue] = productKey[0]?.split(":") ?? [];
                    const nextProductId = selectedProductIdValue ? Number(selectedProductIdValue) : undefined;

                    if (selectedProductIdValue && !Number.isNaN(nextProductId)) {
                      setValue("productId", nextProductId, { shouldValidate: true, shouldDirty: true });
                    }
                    if (selectedAsinValue) {
                      setValue("asin", selectedAsinValue, { shouldValidate: true, shouldDirty: true });
                    }
                  }}
                />
              </VStack>
            : null}
            <Controller
              name="enableInspirationAssetSelection"
              control={control}
              render={({ field }) => (
                <VStack gap={"small"}>
                  <Checkbox
                    value={field.value}
                    onChange={field.onChange}
                    label={t(($) => $.advertising["Select Inspirational Assets (optional)"])}
                  />
                  <small className={css({ color: "gray.600" })}>
                    {t(($) => $.advertising["If unchecked, Amazon will auto-select assets for generation."])}
                  </small>
                </VStack>
              )}
            />
          </VStack>
          {enableInspirationAssetSelection ?
            <div className={css({ width: "100%", paddingY: "large" })}>
              {totalSelectedCount ?
                <SelectedAssetsPreview
                  sections={selectedAssetSections}
                  onRemoveAsset={(sectionKey, assetId) => {
                    if (!(sectionKey in assetTypeLookup)) {
                      return;
                    }
                    const assetType = sectionKey as AssetType;
                    updateSelections(
                      assetType,
                      selectedAssetsByType[assetType].filter((selectedId) => selectedId !== assetId),
                    );
                  }}
                />
              : null}
              <AssetSelector
                marketPlace={{ marketPlace }}
                state={{}}
                products={[]}
                selected={[]}
                selections={currentSelections}
                hideSelectionControls={false}
                hidePreviewField={true}
                hideEligibilityFilter={true}
                hideTagsFilter={true}
                hideAdvancedFilters={true}
                hideGeneratedAssetsFilter={true}
                defaultPageSize={8}
                initialTypeFilter={"LIFESTYLE_IMAGE"}
                onTypeFilterChange={(nextType: AssetType) => setSelectedAssetType(nextType)}
                handleAssetSelection={(updatedSelectionIds: string | string[] | null, _, type: AssetType) => {
                  setSelectedAssetType(type);
                  if (!updatedSelectionIds) {
                    updateSelections(type, []);
                    return;
                  }
                  const normalizedAssetIds =
                    Array.isArray(updatedSelectionIds) ? updatedSelectionIds : [updatedSelectionIds];
                  updateSelections(type, normalizedAssetIds.filter(Boolean));
                }}
                multiSelect={true}
                allowedAssetTypes={["LIFESTYLE_IMAGE", "LOGO", "VIDEO"]}
              />
              {assetErrorsToDisplay.length ?
                <VStack
                  alignItems="start"
                  border={"1px solid {black}"}
                  width={"100%"}
                  paddingY="medium"
                  paddingX={"large"}
                  fontSize="medium"
                  backgroundColor={"yellow/50"}
                >
                  {assetErrorsToDisplay.map((errorMessage, index) => {
                    return <span key={`assetError-${index}`}>{errorMessage}</span>;
                  })}
                </VStack>
              : null}
            </div>
          : null}

          <Flex className={css({ paddingTop: "large" })} gap={"xlarge"} justifyContent={"center"}>
            <Button variant="secondary" onPress={() => setShowModal(false)}>
              {t(($) => $.common.actions.Cancel)}
            </Button>

            <Button
              type="submit"
              isDisabled={
                !formState.isValid || submitVideoGenerationRequest.isPending || !watchedAsin || !watchedProductId
              }
            >
              {t(($) => $.advertising["Generate Videos"])}
            </Button>
          </Flex>
        </form>
      </VStack>
    </Modal>
  );
};

export const VideoGenerationDialogue = ({ asin, productId }: VideoGenerationDialogueProps) => {
  const { t } = useTranslation(["advertising"]);
  const [showModal, setShowModal] = useState(false);

  const userCanUpdateProducts = useSimpleCan("update", "Product");

  return (
    <>
      {showModal && (
        <VideoGenerationDialogueForm
          asin={asin}
          productId={productId}
          showModal={showModal}
          setShowModal={setShowModal}
        />
      )}
      <Flex justifyContent={"center"}>
        <Button onPress={() => setShowModal(true)} isDisabled={!userCanUpdateProducts}>
          {t(($) => $.advertising["Generate AI Videos"])}
        </Button>
      </Flex>
    </>
  );
};
